/**
 * @fileoverview V2 Ingestion State Provider
 *
 * Thin context that holds a useReducer-based state machine and exposes
 * a small, stable API surface. Individual steps do NOT write through
 * this context — they call their own RTK Query mutations directly and
 * only report "done" back here so the wizard can track completion.
 *
 * Save-on-navigate: Steps register a save callback. When the sidebar
 * requests navigation, the provider calls the save callback first,
 * then navigates on completion.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useGetCaseIngestionImportQuery } from '@/services/api/caseIngestionAssistService';
import { useGetCaseIngestionQuery } from '@/services/api/caseIngestionService';
import { STEPS } from '../navigation/steps';
import type { CaseIngestionImport } from '@/services/types/case-ingestion-assist-types';

export interface IngestionState {
  stepIndex: number;
  matterId: string | null;
  phase: 'creation' | 'ingestion';
}

type IngestionAction =
  | { type: 'GO_TO_STEP'; index: number }
  | { type: 'MATTER_CREATED'; matterId: string }
  | { type: 'SET_PHASE'; phase: IngestionState['phase'] };

type SaveCallback = () => Promise<boolean>;

interface IngestionContextValue {
  state: IngestionState;
  dispatch: React.Dispatch<IngestionAction>;
  requestNavigate: (targetIndex: number) => Promise<void>;
  registerSave: (callback: SaveCallback | null) => void;
  importData: CaseIngestionImport | null;
  setMockImport: (data: CaseIngestionImport | null) => void;
  isAssistOpen: boolean;
  setIsAssistOpen: (open: boolean) => void;
}

const IngestionContext = createContext<IngestionContextValue | null>(null);

function reducer(
  state: IngestionState,
  action: IngestionAction,
): IngestionState {
  switch (action.type) {
    case 'GO_TO_STEP':
      return { ...state, stepIndex: action.index };
    case 'MATTER_CREATED':
      return {
        ...state,
        matterId: action.matterId,
        phase: 'ingestion',
        stepIndex: state.stepIndex + 1,
      };
    case 'SET_PHASE':
      return { ...state, phase: action.phase };
    default:
      return state;
  }
}

function getStepIndexById(stepId: string | null): number | null {
  if (!stepId) return null;
  const index = STEPS.findIndex((s) => s.id === stepId);
  return index >= 0 ? index : null;
}

function createInitialState(
  matterId: string | undefined,
  stepFromUrl?: string | null,
): IngestionState {
  if (!matterId) {
    return {
      stepIndex: 0,
      matterId: null,
      phase: 'creation',
    };
  }

  const urlStep = getStepIndexById(stepFromUrl ?? null);
  return {
    stepIndex: urlStep ?? 1,
    matterId,
    phase: 'ingestion',
  };
}

interface IngestionProviderProps {
  children: React.ReactNode;
}

export const IngestionProvider = ({
  children,
}: IngestionProviderProps): React.JSX.Element => {
  const { matterId: matterIdParam } = useParams<{ matterId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize step from URL query param
  const initialStepFromUrl = searchParams.get('step');
  const [state, dispatch] = useReducer(
    reducer,
    createInitialState(matterIdParam, initialStepFromUrl),
  );
  const saveRef = useRef<SaveCallback | null>(null);
  const lastWrittenStep = useRef<string | null>(null);

  // Sync step index → URL
  useEffect(() => {
    const stepId = STEPS[state.stepIndex]?.id ?? 'basics';
    if (lastWrittenStep.current === stepId) return;
    lastWrittenStep.current = stepId;
    setSearchParams({ step: stepId }, { replace: true });
  }, [state.stepIndex, setSearchParams]);
  const [mockImport, setMockImport] = useState<CaseIngestionImport | null>(null);
  const [isAssistOpen, setIsAssistOpen] = useState(false);

  const { data: fetchedImportData, refetch: refetchImport } =
    useGetCaseIngestionImportQuery(state.matterId ?? skipToken);

  // Poll ingestion status to detect completion
  const { data: ingestionResult } = useGetCaseIngestionQuery(
    state.matterId ?? skipToken,
  );

  // Refetch import data + auto-open sidebar when ingestion completes
  const prevIngestionStatus = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const currentStatus = ingestionResult?.ingestion_status;
    if (
      prevIngestionStatus.current !== 'completed' &&
      currentStatus === 'completed' &&
      state.matterId
    ) {
      refetchImport();
      setIsAssistOpen(true);
    }
    // Also auto-open when ingestion starts
    if (
      (currentStatus === 'pending' || currentStatus === 'in_progress') &&
      !isAssistOpen
    ) {
      setIsAssistOpen(true);
    }
    prevIngestionStatus.current = currentStatus;
  }, [ingestionResult?.ingestion_status, state.matterId, refetchImport, isAssistOpen]);

  const importData = useMemo(
    () => mockImport ?? fetchedImportData ?? null,
    [fetchedImportData, mockImport],
  );

  const registerSave = useCallback((callback: SaveCallback | null): void => {
    saveRef.current = callback;
  }, []);

  const requestNavigate = useCallback(async (targetIndex: number): Promise<void> => {
    if (saveRef.current) {
      const didSave = await saveRef.current();
      if (!didSave) {
        return;
      }
    }

    dispatch({ type: 'GO_TO_STEP', index: targetIndex });
  }, []);

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      requestNavigate,
      registerSave,
      importData,
      setMockImport,
      isAssistOpen,
      setIsAssistOpen,
    }),
    [importData, registerSave, requestNavigate, state, isAssistOpen],
  );

  return (
    <IngestionContext.Provider value={contextValue}>
      {children}
    </IngestionContext.Provider>
  );
}

export function useIngestion(): IngestionContextValue {
  const context = useContext(IngestionContext);

  if (!context) {
    throw new Error('useIngestion must be used within IngestionProvider');
  }

  return context;
}
